var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, memo, useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon';
import { removeDiacriticsFromString } from './Select.utils';
import { InputWrapper } from '../InputWrapper';
import SelectDropdown from './SelectDropdown/SelectDropdown';
import { Popup } from '../Popup';
import useScreenWidthInfo from '../_shared/hooks/useScreenWidthInfo';
import { getDropdownPosition } from '../Dropdown/Dropdown.utils';
import './Select.styles.scss';
import { composeRef } from 'rc-util/lib/ref';
var EMappedInputEventCodes;
(function (EMappedInputEventCodes) {
    EMappedInputEventCodes["ARROW_DOWN"] = "ArrowDown";
    EMappedInputEventCodes["ARROW_UP"] = "ArrowUp";
    EMappedInputEventCodes["ENTER"] = "Enter";
    EMappedInputEventCodes["ESC"] = "Escape";
    EMappedInputEventCodes["TAB"] = "Tab";
})(EMappedInputEventCodes || (EMappedInputEventCodes = {}));
var Select = forwardRef(function Select(_a, ref) {
    var _b, _c, _d;
    var badgeStyles = _a.badgeStyles, disabled = _a.disabled, dropdownPosition = _a.dropdownPosition, helperText = _a.helperText, helperTextTooltipPlacement = _a.helperTextTooltipPlacement, _e = _a.hideLabel, hideLabel = _e === void 0 ? false : _e, _f = _a.isMultipleSelect, isMultipleSelect = _f === void 0 ? false : _f, _g = _a.isTimePicker, isTimePicker = _g === void 0 ? false : _g, label = _a.label, name = _a.name, onBlur = _a.onBlur, onFocus = _a.onFocus, onSelect = _a.onSelect, options = _a.options, placeholder = _a.placeholder, readOnly = _a.readOnly, required = _a.required, supportMessage = _a.supportMessage, _h = _a.tabIndex, tabIndex = _h === void 0 ? 0 : _h, value = _a.value, variant = _a.variant, props = __rest(_a, ["badgeStyles", "disabled", "dropdownPosition", "helperText", "helperTextTooltipPlacement", "hideLabel", "isMultipleSelect", "isTimePicker", "label", "name", "onBlur", "onFocus", "onSelect", "options", "placeholder", "readOnly", "required", "supportMessage", "tabIndex", "value", "variant"]);
    var inputRef = useRef(null);
    var containerRef = useRef(null);
    var optionsRef = useRef(null);
    var selectedOptionsRef = useRef(null);
    var myRef = useRef(null);
    var _j = useState(), currentDropdownPosition = _j[0], setCurrentDropdownPosition = _j[1];
    var _k = useState(options), filteredSelectOptions = _k[0], setFilteredSelectOptions = _k[1];
    var getSelectedOptionFromValue = function (selectedValue) {
        return options.find(function (opt) { return opt.value.toString() === selectedValue.toString(); });
    };
    var _l = useState((value !== null && value !== void 0 ? value : (_b = myRef.current) === null || _b === void 0 ? void 0 : _b.value) && !isMultipleSelect
        ? getSelectedOptionFromValue((value !== null && value !== void 0 ? value : (_c = myRef.current) === null || _c === void 0 ? void 0 : _c.value))
        : undefined), selectedOption = _l[0], setSelectedOption = _l[1];
    var getMultipleSelectedOptionsFromValue = function (selectedValues) {
        if (isMultipleSelect && typeof value === 'string') {
            console.error('Hefesto - alert: Multiple select requires a string[] as value.');
            return;
        }
        var selectedOptions = [];
        selectedValues.forEach(function (val) {
            var option = getSelectedOptionFromValue(val);
            if (option) {
                selectedOptions.push(option);
            }
        });
        return selectedOptions;
    };
    var _m = useState(value && isMultipleSelect
        ? getMultipleSelectedOptionsFromValue(value)
        : undefined), multiSelectedOptions = _m[0], setMultiSelectedOptions = _m[1];
    var _o = useState(''), inputValue = _o[0], setInputValue = _o[1];
    var checkIfValueChangedFromOut = function () {
        var _a;
        if (isMultipleSelect && !!value && !!multiSelectedOptions) {
            var isMultiSelectedOptionsChanged = (_a = multiSelectedOptions.length !== value.length) !== null && _a !== void 0 ? _a : multiSelectedOptions
                .map(function (opt) { return opt.value; })
                .some(function (val) { return !value.includes(val); });
            if (isMultiSelectedOptionsChanged) {
                setMultiSelectedOptions(getMultipleSelectedOptionsFromValue(value));
            }
        }
        else if (!isMultipleSelect) {
            var selectedValue = value !== null && value !== void 0 ? value : (myRef.current ? myRef.current.value : undefined);
            if (!!selectedValue &&
                (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value.toString()) !== selectedValue) {
                onSelectOption(selectedValue);
            }
            if (!selectedValue && !!selectedOption) {
                cleanSelectedOption();
            }
        }
    };
    useEffect(function () {
        checkIfValueChangedFromOut();
    }, [value, options]);
    var cleanSelectedOption = function () {
        setInputValue('');
        setSelectedOption(undefined);
    };
    var updateRefValue = function (value) {
        if (myRef.current) {
            myRef.current.value = value;
            myRef.current.focus();
            myRef.current.blur();
        }
    };
    var updateSelectedOption = function (option) {
        setSelectedOption(option);
        !!onSelect && onSelect(option.value.toString());
        onCloseOptions();
        updateRefValue(option.value.toString());
    };
    var updateMultiSelectedOptions = function (option) {
        var newMultiSelectedOptions = multiSelectedOptions !== null && multiSelectedOptions !== void 0 ? multiSelectedOptions : [];
        var idx = newMultiSelectedOptions.findIndex(function (opt) { return opt.value === option.value; });
        if (idx === -1) {
            newMultiSelectedOptions.push(option);
        }
        else {
            newMultiSelectedOptions.splice(idx, 1);
        }
        setMultiSelectedOptions(__spreadArray([], newMultiSelectedOptions, true));
        !!onSelect &&
            onSelect(newMultiSelectedOptions.map(function (opt) { return opt.value.toString(); }));
        setFocusOnInput();
    };
    var onChangeTextField = function (typedText) {
        setInputValue(typedText);
        onOpenOption();
        filterOptions(typedText);
    };
    var filterOptions = function (typedText) {
        var lowerCaseTypedText = removeDiacriticsFromString(typedText).toLowerCase();
        var filteredOptions = options.filter(function (opt) {
            var _a;
            return removeDiacriticsFromString(opt.label)
                .toLowerCase()
                .includes(lowerCaseTypedText) ||
                ((_a = opt.descriptions) === null || _a === void 0 ? void 0 : _a.some(function (desc) {
                    return removeDiacriticsFromString(desc)
                        .toLowerCase()
                        .includes(lowerCaseTypedText);
                }));
        });
        cleanIsHover();
        setFilteredSelectOptions(filteredOptions);
    };
    var _p = useState(-1), optionHoveredIdx = _p[0], setOptionHoveredIdx = _p[1];
    var updateCustomSelectHovered = function (newIndex) {
        var _a, _b, _c;
        var prevOption = (_a = optionsRef.current) === null || _a === void 0 ? void 0 : _a.children[optionHoveredIdx];
        var option = (_b = optionsRef.current) === null || _b === void 0 ? void 0 : _b.children[newIndex];
        if (prevOption) {
            prevOption.classList.remove('is-hover');
        }
        if (option) {
            option.classList.add('is-hover');
            var _d = option, offsetTop = _d.offsetTop, offsetHeight = _d.offsetHeight;
            (_c = optionsRef.current) === null || _c === void 0 ? void 0 : _c.scroll(0, offsetTop - offsetHeight);
        }
        setOptionHoveredIdx(newIndex);
    };
    var scrollIntoSelectedOption = function () {
        var scrollIntoOption = function (currentSelectedOption) {
            setTimeout(function () {
                var _a;
                var allOptions = (_a = optionsRef.current) === null || _a === void 0 ? void 0 : _a.children;
                if (allOptions) {
                    Array.from(allOptions).forEach(function (option) {
                        var _a;
                        var optionValue = option.getAttribute('value');
                        if (optionValue &&
                            optionValue === currentSelectedOption.value.toString()) {
                            var _b = option, offsetTop = _b.offsetTop, offsetHeight = _b.offsetHeight;
                            (_a = optionsRef.current) === null || _a === void 0 ? void 0 : _a.scroll(0, offsetTop - offsetHeight);
                        }
                    });
                }
            }, 0);
        };
        if (isMultipleSelect &&
            !!multiSelectedOptions &&
            (multiSelectedOptions === null || multiSelectedOptions === void 0 ? void 0 : multiSelectedOptions.length) > 0) {
            scrollIntoOption(multiSelectedOptions[0]);
        }
        else if (!isMultipleSelect && selectedOption) {
            scrollIntoOption(selectedOption);
        }
    };
    var isSmallerThanLayoutWidthMD = useScreenWidthInfo().isSmallerThanLayoutWidthMD;
    var onOpenOption = function () {
        var _a;
        if ((_a = disabled !== null && disabled !== void 0 ? disabled : readOnly) !== null && _a !== void 0 ? _a : !!currentDropdownPosition)
            return;
        setFocusOnInput();
        setFilteredSelectOptions(options);
        setCurrentDropdownPosition(dropdownPosition !== null && dropdownPosition !== void 0 ? dropdownPosition : getDropdownPosition(containerRef));
        scrollIntoSelectedOption();
    };
    var setFocusOnInput = function () {
        var _a;
        !isSmallerThanLayoutWidthMD && ((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus());
    };
    var onSelectOption = function (newOptionValue) {
        if (newOptionValue) {
            var newOption = getSelectedOptionFromValue(newOptionValue);
            if (newOption) {
                if (isMultipleSelect) {
                    updateMultiSelectedOptions(newOption);
                }
                else {
                    updateSelectedOption(newOption);
                }
            }
        }
    };
    var onCloseOptions = function () {
        setInputValue('');
        setCurrentDropdownPosition(undefined);
        cleanIsHover();
        setFilteredSelectOptions(options);
    };
    var cleanIsHover = function () {
        var _a;
        var hoveredOption = (_a = optionsRef.current) === null || _a === void 0 ? void 0 : _a.children[optionHoveredIdx];
        if (hoveredOption) {
            hoveredOption.classList.remove('is-hover');
            setOptionHoveredIdx(-1);
        }
    };
    var checkOutsideClick = function (event) {
        if (!isSmallerThanLayoutWidthMD &&
            !!currentDropdownPosition &&
            !!event &&
            !!containerRef.current &&
            !containerRef.current.contains(event.target)) {
            onCloseOptions();
        }
    };
    useEffect(function () {
        window.addEventListener('click', checkOutsideClick);
        return function () {
            window.removeEventListener('click', checkOutsideClick);
        };
    });
    var onKeyDown = function (event) {
        var _a;
        if (!Object.values(EMappedInputEventCodes).find(function (code) { return code === event.code; })) {
            return;
        }
        event.preventDefault();
        switch (event.code) {
            // press down -> go next
            case EMappedInputEventCodes.ARROW_DOWN: {
                if (optionHoveredIdx < filteredSelectOptions.length - 1) {
                    if (!currentDropdownPosition) {
                        onOpenOption();
                        updateCustomSelectHovered(-1);
                    }
                    else {
                        updateCustomSelectHovered(optionHoveredIdx + 1);
                    }
                }
                return;
            }
            // press up -> go previous
            case EMappedInputEventCodes.ARROW_UP: {
                if (!currentDropdownPosition) {
                    onOpenOption();
                    updateCustomSelectHovered(-1);
                }
                else if (optionHoveredIdx === -1) {
                    updateCustomSelectHovered(filteredSelectOptions.length - 1);
                }
                else if (optionHoveredIdx > 0) {
                    updateCustomSelectHovered(optionHoveredIdx - 1);
                }
                return;
            }
            // press Enter -> select the option
            case EMappedInputEventCodes.ENTER: {
                if (!currentDropdownPosition) {
                    onOpenOption();
                    return;
                }
                var option = (_a = optionsRef.current) === null || _a === void 0 ? void 0 : _a.children[optionHoveredIdx];
                var optionValue = option === null || option === void 0 ? void 0 : option.getAttribute('value');
                onSelectOption(optionValue !== null && optionValue !== void 0 ? optionValue : undefined);
                return;
            }
            // press ESC | TAB -> close
            case EMappedInputEventCodes.TAB:
            case EMappedInputEventCodes.ESC: {
                onCloseOptions();
            }
        }
    };
    var getContainerClassName = function () {
        var classList = ['hefesto-select'];
        if (variant) {
            classList.push("hefesto-select--".concat(variant));
        }
        if (disabled) {
            classList.push('select--disabled');
        }
        if (currentDropdownPosition) {
            classList.push('select--focused');
        }
        if (hideLabel) {
            classList.push('hefesto-select--hide-label');
        }
        return classList.join(' ');
    };
    var containerClassName = getContainerClassName();
    var getInputClassName = function () {
        var classList = ['hefesto-select__input'];
        if (currentDropdownPosition) {
            classList.push("open-".concat(currentDropdownPosition));
        }
        return classList.join(' ');
    };
    var inputClassName = getInputClassName();
    var getIconClassName = function () {
        var classList = ['input-helpers__right--icon'];
        if (currentDropdownPosition) {
            classList.push('icon-open');
        }
        return classList.join(' ');
    };
    var iconClassName = getIconClassName();
    var getInputIconVariant = function () {
        if (readOnly) {
            return 'tertiary';
        }
        else if (!currentDropdownPosition && variant) {
            return variant;
        }
        else {
            return 'primary';
        }
    };
    var updateBadge = function () {
        var _a, _b;
        if (!selectedOptionsRef.current) {
            return;
        }
        var elements = Array.from(selectedOptionsRef.current.getElementsByClassName('input-helpers__selected-options--option'));
        var optionsWidth = 0;
        var parentOffsetWidth = (_b = (_a = selectedOptionsRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0;
        elements.forEach(function (e, idx) {
            e.classList.remove('hide');
            e.classList.remove('with-comma');
            var offsetWidth = e.offsetWidth;
            if (idx > 0 && optionsWidth + Number(offsetWidth) >= parentOffsetWidth) {
                e.classList.add('hide');
            }
            optionsWidth += offsetWidth;
        });
        for (var _i = 0, elements_1 = elements; _i < elements_1.length; _i++) {
            var e = elements_1[_i];
            var nextSibling = e.nextElementSibling;
            if (!!nextSibling && !nextSibling.classList.contains('hide')) {
                e.classList.add('with-comma');
            }
        }
        var ellipsedItems = Array.from(selectedOptionsRef.current.getElementsByClassName('hide'));
        setBadgeValue(ellipsedItems.length);
    };
    var _q = useState(0), badgeValue = _q[0], setBadgeValue = _q[1];
    useEffect(function () {
        if (isMultipleSelect && multiSelectedOptions) {
            updateBadge();
        }
    }, [isMultipleSelect, multiSelectedOptions]);
    var handleResize = function () {
        if (isMultipleSelect && multiSelectedOptions) {
            updateBadge();
        }
    };
    useEffect(function () {
        window.addEventListener('resize', handleResize);
        return function () {
            window.removeEventListener('resize', handleResize);
        };
    });
    return (_jsx(Popup, __assign({ trigger: 'click', popupPlacement: "bottomLeft", popupVisible: !!currentDropdownPosition, onPopupVisibleChange: function (visible) {
            if (visible) {
                onCloseOptions();
            }
        }, popupContainerScreenPosition: (_d = containerRef.current) === null || _d === void 0 ? void 0 : _d.getBoundingClientRect().left, popup: _jsx(_Fragment, { children: currentDropdownPosition && (_jsx(SelectDropdown, { closeDropdopwn: onCloseOptions, containerRef: containerRef, dropdownPosition: currentDropdownPosition, filteredSelectOptions: filteredSelectOptions, inputRef: inputRef, isMultipleSelect: isMultipleSelect, label: label, multiSelectedOptions: multiSelectedOptions, onChangeSearchTerm: onChangeTextField, onMouseOver: cleanIsHover, onSelectOption: onSelectOption, optionsRef: optionsRef, searchTerm: inputValue, selectedOption: selectedOption })) }) }, { children: _jsxs(InputWrapper, __assign({ disabled: disabled, helperText: helperText, helperTextTooltipPlacement: helperTextTooltipPlacement, hideLabel: hideLabel, label: label, supportMessage: supportMessage, variant: variant, name: name, required: required }, { children: [_jsxs("select", __assign({ className: "hefesto-select__fake-select", ref: composeRef(ref, myRef), multiple: isMultipleSelect, name: name, onBlur: function (e) {
                        !!onBlur &&
                            onBlur(e);
                    }, value: isMultipleSelect
                        ? multiSelectedOptions === null || multiSelectedOptions === void 0 ? void 0 : multiSelectedOptions.map(function (opt) { return opt.value; })
                        : selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value }, { children: [_jsx("option", { value: "" }), options.map(function (opt) { return (_jsx("option", __assign({ value: opt.value }, { children: opt.value }), opt.value)); })] })), _jsxs("div", __assign({ className: containerClassName, ref: containerRef, "data-testid": "hefesto-select-container", onClick: onOpenOption }, { children: [_jsx("input", __assign({}, props, { className: inputClassName, disabled: disabled, readOnly: readOnly, type: "text", id: name, name: name, onChange: function (e) {
                                onChangeTextField(e.target.value);
                            }, onKeyDown: onKeyDown, value: inputValue, placeholder: (!!multiSelectedOptions && multiSelectedOptions.length > 0) ||
                                !!selectedOption
                                ? ''
                                : placeholder !== null && placeholder !== void 0 ? placeholder : '- Selecione -', ref: inputRef, onFocus: function (event) {
                                var _a;
                                !!onFocus && onFocus(event);
                                onOpenOption();
                                if (isSmallerThanLayoutWidthMD) {
                                    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
                                }
                            }, onBlur: onBlur, tabIndex: tabIndex, autoComplete: "off", "data-testid": "hefesto-select-input" })), _jsxs("div", __assign({ className: "input-helpers" }, { children: [_jsxs("div", __assign({ ref: selectedOptionsRef, className: "input-helpers__selected-options", style: { opacity: inputValue === '' ? 1 : 0 } }, { children: [!!multiSelectedOptions &&
                                            multiSelectedOptions.map(function (item, idx) { return (_jsx("span", __assign({ className: "input-helpers__selected-options--option", onClick: function () { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); } }, { children: item.label }), idx)); }), !isMultipleSelect && selectedOption && (_jsx("span", __assign({ className: "input-helpers__selected-options--option", onClick: function () { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); } }, { children: selectedOption.label })))] })), _jsxs("div", __assign({ className: "input-helpers__right" }, { children: [badgeValue > 0 && (_jsxs("span", __assign({ style: badgeStyles, className: "input-helpers__badge" }, { children: ["+", badgeValue] }))), _jsx("i", __assign({ className: iconClassName, onClick: function () {
                                                if (currentDropdownPosition) {
                                                    onCloseOptions();
                                                }
                                                else {
                                                    onOpenOption();
                                                }
                                            } }, { children: _jsx(Icon, { option: isTimePicker ? 'Clock' : 'CaretDown', variant: getInputIconVariant(), size: "sm" }) }))] }))] }))] }))] })) })));
});
export default memo(Select);
